/**
 * sessionToken.util — single source of truth cho session token refresh,
 * dùng CHUNG bởi axiosClient (HTTP 401) và useElearningSession (socket
 * connect_error) để hai cơ chế KHÔNG xung đột.
 *
 * Bối cảnh bug: socket auth đọc sessionToken 1 lần lúc mount (closure) →
 * khi axiosClient refresh token mới, socket vẫn dùng token cũ → reconnect
 * bằng token hết hạn → server reject → socket logout thẳng, kéo cả phiên
 * (dù refreshToken 30d vẫn còn hạn). Fix:
 *   1. getActiveToken() đọc localStorage MỖI lần (không cache trong closure).
 *   2. refreshSession() dùng chung 1 pending promise → mọi caller (axios +
 *      socket) chia sẻ 1 lần refresh, lưu pair mới vào localStorage.
 *   3. isExpiredTokenError() chuẩn hóa nhận diện lỗi token từ socket.
 */
import axios from 'axios';

const currentPath = typeof window !== 'undefined' ? window.location.hostname : '';
const baseURL = currentPath?.split('.')?.includes('lms2')
  ? process.env.REACT_APP_API_2_URL
  : process.env.REACT_APP_API_URL;

/** Đọc token đang hiệu lực: ưu tiên sessionToken, fallback moodleToken. */
export const getActiveToken = () => {
  try {
    return (
      localStorage.getItem('sessionToken') ||
      localStorage.getItem('moodleToken') ||
      localStorage.getItem('user-jwt-tk') ||
      null
    );
  } catch (_) {
    return null;
  }
};

// Các message socket server trả khi token không hợp lệ/hết hạn — phải khớp
// với session-socket.service.js (BE) `next(new Error('Invalid or expired token'))`.
const EXPIRED_PATTERNS = [
  'Invalid or expired token',
  'Invalid moodleToken',
  'token or moodleToken is required',
  'Driving student not found',
];

export const isExpiredTokenError = (msg) => {
  if (!msg || typeof msg !== 'string') return false;
  return EXPIRED_PATTERNS.some((p) => msg.includes(p));
};

// Pending refresh dùng chung: 100 caller đồng thời → 1 request /elearning/refresh.
let _pendingRefresh = null;

/** Test helper — reset state giữa các test. */
export const __resetPendingRefresh = () => { _pendingRefresh = null; };

/**
 * Đổi refreshToken lấy sessionToken mới. Lưu pair mới vào localStorage.
 * Throw nếu không có refreshToken (caller phải logout) hoặc response hỏng.
 * Concurrent call chia sẻ cùng promise.
 */
export const refreshSession = () => {
  if (_pendingRefresh) return _pendingRefresh;

  _pendingRefresh = (async () => {
    const refreshToken = (() => { try { return localStorage.getItem('refreshToken'); } catch (_) { return null; } })();
    if (!refreshToken) throw new Error('no_refresh_token');

    // axios raw (không qua interceptor) tránh đệ quy 401 → refresh → 401.
    const rawAxios = axios.create({ baseURL });
    const res = await rawAxios.post('/elearning/refresh', { refreshToken });
    const data = res?.data?.data ?? res?.data ?? {};
    const newSessionToken = data.sessionToken;
    const newRefreshToken = data.refreshToken;
    if (!newSessionToken || !newRefreshToken) throw new Error('malformed_refresh_response');

    try { localStorage.setItem('sessionToken', newSessionToken); } catch (_) { /* noop */ }
    try { localStorage.setItem('refreshToken', newRefreshToken); } catch (_) { /* noop */ }
    return newSessionToken;
  })().finally(() => { _pendingRefresh = null; });

  return _pendingRefresh;
};
